import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { validateAiConfig, defaultBaseUrl, workOrderGeneratePolicyCaption } from './aiConfigForm.logic';

interface AiConfig {
  provider: string;
  model: string;
  base_url: string;
  api_key: string | null;
  num_predict: number;
  timeout_seconds: number;
  temperature: number;
  max_racks_in_context: number;
  max_equipment_in_context: number;
  max_prompt_chars: number;
  max_chunks: number;
  use_structured_output: boolean;
  fallback_provider: string | null;
  fallback_model: string | null;
}

interface StructuredOutputStatus {
  supported: boolean;
  version: string | null;
  active: boolean;
}

interface TestResult {
  ok: boolean;
  model: string;
  response_time_ms: number;
  error: string | null;
}

const FIELD_STYLE: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
};
const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12, fontWeight: 500, color: '#9ca3af',
};
const INPUT_STYLE: React.CSSProperties = {
  background: '#111827', border: '1px solid #374151', borderRadius: 6,
  color: '#f3f4f6', fontSize: 13, padding: '7px 10px', width: '100%', boxSizing: 'border-box',
};
const BTN_PRIMARY: React.CSSProperties = {
  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
  padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const BTN_SECONDARY: React.CSSProperties = {
  background: '#1f2937', color: '#d1d5db', border: '1px solid #374151', borderRadius: 6,
  padding: '8px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={FIELD_STYLE}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
    </div>
  );
}

export function AiConfigForm() {
  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [soStatus, setSoStatus] = useState<StructuredOutputStatus | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await api.get('/api/admin/integrations/ai-config');
      setCfg({ ...res.data, api_key: res.data.api_key ?? '' });
    } catch {
      toast.error('Не удалось загрузить AI-конфигурацию');
    }
    // P3-1: бейдж grammar sampling — не блокируем форму при ошибке
    api.get('/api/admin/integrations/ai-config/structured-output')
      .then(res => setSoStatus(res.data))
      .catch(() => {});
  }

  function update<K extends keyof AiConfig>(key: K, value: AiConfig[K]) {
    setCfg(prev => {
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
      if (key === 'provider') {
        next.base_url = defaultBaseUrl(value as string, prev.base_url);
      }
      return next;
    });
    setValidationError(null);
    setTestResult(null);
  }

  async function handleSave() {
    if (!cfg) return;
    const err = validateAiConfig({ ...cfg, api_key: cfg.api_key ?? '' });
    if (err) { setValidationError(err); return; }
    setSaving(true);
    try {
      // Only send api_key if non-empty (preserve existing if blank)
      const body: Record<string, unknown> = { ...cfg };
      if (!cfg.api_key?.trim()) delete body.api_key;
      await api.put('/api/admin/integrations/ai-config', body);
      toast.success('AI-конфигурация сохранена');
    } catch {
      toast.error('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/api/admin/integrations/ai-config/test');
      setTestResult(res.data);
    } catch (e: any) {
      setTestResult({ ok: false, model: cfg?.model ?? '', response_time_ms: 0, error: String(e) });
    } finally {
      setTesting(false);
    }
  }

  if (!cfg) return <div style={{ color: '#6b7280', fontSize: 13 }}>Загрузка...</div>;

  const isOpenAI = cfg.provider === 'openai';

  return (
    <div style={{ maxWidth: 560 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: '#f3f4f6', margin: '0 0 12px' }}>
        AI / LLM конфигурация
      </h2>

      <p
        data-testid="work-order-generate-policy"
        style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.45, margin: '0 0 16px' }}
      >
        {workOrderGeneratePolicyCaption()}
      </p>

      {/* P3-1: structured output badge */}
      {soStatus && (
        <div
          data-testid="structured-output-badge"
          style={{
            display: 'inline-block', fontSize: 11, fontWeight: 600, marginBottom: 16,
            padding: '3px 10px', borderRadius: 10,
            background: soStatus.active && soStatus.supported ? '#052e16' : soStatus.supported ? '#172554' : '#1f0f0f',
            border: `1px solid ${soStatus.active && soStatus.supported ? '#166534' : soStatus.supported ? '#1d4ed8' : '#7f1d1d'}`,
            color: soStatus.active && soStatus.supported ? '#86efac' : soStatus.supported ? '#93c5fd' : '#f87171',
          }}
        >
          {soStatus.active && soStatus.supported
            ? `✓ grammar sampling active${soStatus.version ? ` (Ollama ${soStatus.version})` : ''}`
            : soStatus.supported
              ? 'grammar sampling доступен — включите use_structured_output'
              : 'grammar sampling недоступен — обновите Ollama до ≥ 0.5.0'}
        </div>
      )}

      {/* Provider */}
      <Field label="Провайдер">
        <select value={cfg.provider} onChange={e => update('provider', e.target.value)}
          style={{ ...INPUT_STYLE, appearance: 'auto' }}>
          <option value="ollama">Ollama (локальный)</option>
          <option value="openai">OpenAI-compatible</option>
        </select>
      </Field>

      {/* Model */}
      <Field label="Модель">
        <input value={cfg.model} onChange={e => update('model', e.target.value)}
          placeholder="qwen2.5:7b" style={INPUT_STYLE} />
      </Field>

      {/* Base URL */}
      <Field label="Base URL">
        <input value={cfg.base_url} onChange={e => update('base_url', e.target.value)}
          style={INPUT_STYLE} />
      </Field>

      {/* API Key — only for openai */}
      {isOpenAI && (
        <Field label="API Key">
          <input type="password" value={cfg.api_key ?? ''} onChange={e => update('api_key', e.target.value)}
            placeholder="sk-..." style={INPUT_STYLE} />
        </Field>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Макс. токенов в ответе (256–8192)">
          <input type="number" min={256} max={8192} value={cfg.num_predict}
            onChange={e => update('num_predict', Number(e.target.value))} style={INPUT_STYLE} />
        </Field>
        <Field label="Таймаут AI (сек) (30–600)">
          <input type="number" min={30} max={600} value={cfg.timeout_seconds}
            onChange={e => update('timeout_seconds', Number(e.target.value))} style={INPUT_STYLE} />
        </Field>
        <Field label="Температура (0–1)">
          <input type="number" min={0} max={1} step={0.1} value={cfg.temperature}
            onChange={e => update('temperature', Number(e.target.value))} style={INPUT_STYLE} />
        </Field>
        <Field label="Макс. стоек в контексте (1–20)">
          <input type="number" min={1} max={20} value={cfg.max_racks_in_context}
            onChange={e => update('max_racks_in_context', Number(e.target.value))} style={INPUT_STYLE} />
        </Field>
        <Field label="Макс. оборудования в контексте (1–50)">
          <input type="number" min={1} max={50} value={cfg.max_equipment_in_context}
            onChange={e => update('max_equipment_in_context', Number(e.target.value))} style={INPUT_STYLE} />
        </Field>
        <Field label="Макс. символов в промпте (1000–10000)">
          <input type="number" min={1000} max={10000} value={cfg.max_prompt_chars}
            onChange={e => update('max_prompt_chars', Number(e.target.value))} style={INPUT_STYLE} />
        </Field>
      </div>

      {/* P2-5: fallback provider */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Fallback-провайдер (при сбое основного)">
          <select
            value={cfg.fallback_provider ?? ''}
            onChange={e => update('fallback_provider', e.target.value || null)}
            style={{ ...INPUT_STYLE, appearance: 'auto' }}
          >
            <option value="">— выключен —</option>
            <option value="ollama">Ollama</option>
            <option value="openai">OpenAI-compatible</option>
          </select>
        </Field>
        <Field label="Fallback-модель">
          <input
            value={cfg.fallback_model ?? ''}
            onChange={e => update('fallback_model', e.target.value || null)}
            placeholder="gpt-4o-mini"
            style={INPUT_STYLE}
            disabled={!cfg.fallback_provider}
          />
        </Field>
      </div>

      {/* Validation error */}
      {validationError && (
        <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12, padding: '6px 10px',
          background: '#1f0f0f', borderRadius: 4, border: '1px solid #7f1d1d' }}>
          {validationError}
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div style={{
          fontSize: 12, marginBottom: 12, padding: '8px 12px', borderRadius: 4,
          background: testResult.ok ? '#052e16' : '#1f0f0f',
          border: `1px solid ${testResult.ok ? '#166534' : '#7f1d1d'}`,
          color: testResult.ok ? '#86efac' : '#f87171',
        }}>
          {testResult.ok
            ? `✓ OK — ${testResult.model} ответил за ${testResult.response_time_ms}мс`
            : `✗ Ошибка: ${testResult.error}`}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button onClick={handleTest} disabled={testing} style={BTN_SECONDARY}>
          {testing ? 'Проверка...' : 'Тест соединения'}
        </button>
        <button onClick={handleSave} disabled={saving} style={BTN_PRIMARY}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
